/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, filter, first, map, of, Subject, switchMap } from 'rxjs';
import { ApplicationState } from "../../../service/application-state.service";
import { Team } from "../../../concept/team";
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';
import { FormActionsComponent, FormComponent, requiredValidator, SpinnerComponent } from "typedb-cloud-framework";
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { SnackbarService } from "../../../service/snackbar.service";
import { AuthorizationService } from "../../../service/authorization.service";
import { hasAdminAccess } from "../../../concept/iam";
import { MatTooltipModule } from '@angular/material/tooltip';
import { TeamCreationDialogComponent } from "../create/team-creation-dialog.component";
import { MatDialog } from '@angular/material/dialog';

function teamOptionOf(team: Team, enabled: boolean) {
    return { value: team, viewValue: `${team.id}${enabled ? "" : " (Admin access required)"}`, enabled }
}

@Component({
    selector: 'tp-marketplace-integration',
    imports: [
        MatSelectModule, AsyncPipe, FormComponent,
        FormActionsComponent, ReactiveFormsModule, MatTooltipModule, SpinnerComponent
    ],
    templateUrl: './marketplace-integration.component.html',
    styleUrl: './marketplace-integration.component.scss'
})
export class MarketplaceIntegrationComponent implements OnInit {
    @ViewChild(MatSelect) teamSelector!: MatSelect;
    @Input({ required: true }) providerName?: string;
    @Input({ required: true }) isSubmitting$!: Subject<boolean>;
    @Output() setAccount = new EventEmitter<{ team: Team, token: string }>();

    constructor(
        public app: ApplicationState, private route: ActivatedRoute,
        private formBuilder: FormBuilder, private snackbar: SnackbarService,
        private authorization: AuthorizationService, private dialog: MatDialog
    ) { }

    buttonIdPrefix = `${this.providerName?.toLowerCase()}MarketplaceIntegration`
    private token?: string

    getAccessLevels(teams: Team[]) {
        return this.authorization.teamAccessLevels(teams.map(x => x.uuid))
    }

    readonly teamOptions$ = this.app.currentUserTeams$.pipe(
        filter(teams => !!teams), map(teams => teams!),
        switchMap(teams => combineLatest([of(teams), this.getAccessLevels(teams)])),
        map(([teams, accessLevels]) =>
            teams.map((x, idx) => teamOptionOf(x, hasAdminAccess(accessLevels[idx])))
        )
    )
    readonly form = this.formBuilder.group({
        team: [null as Team | null, [requiredValidator]]
    })

    ngOnInit() {
        this.route.queryParamMap
            .pipe(filter(queryParams => queryParams.has("token")))
            .subscribe(queryParams => { this.token = decodeURIComponent(queryParams.get("token")!) })
        this.teamOptions$.pipe(first()).subscribe((teams) => {
            this.form.patchValue({ team: teams[0].value })
        })
    }

    onCreateTeamClick() {
        this.teamSelector.close();
        this.dialog.open(TeamCreationDialogComponent)
            .componentInstance.redirectAfterClose = false;
    }

    onSubmitConnectTeamForm() {
        if (!this.token) {
            this.isSubmitting$.next(false);
            this.snackbar.errorPersistent("No token found in URL parameter");
            return;
        }
        const form = this.form.value;
        const team = form.team!;
        this.setAccount.emit({ team, token: this.token })
    }
}
